import { obj, arr, str, type Json } from "./json";

export type MediaKind = "image" | "video" | "audio" | "file";

/** 消息里的一个媒体文件：服务器上的路径、网络地址或 data URL（对应 HermesKit 的 MediaRef）。 */
export interface MediaRef {
  /** 原样保留的来源（`/home/.../a.png`、`https://.../v.mp4`、`data:image/png;base64,...`） */
  source: string;
  kind: MediaKind;
}

const imageExtensions = new Set(["png", "jpg", "jpeg", "gif", "webp", "bmp", "heic", "heif", "tif", "tiff"]);
const videoExtensions = new Set(["mp4", "mov", "m4v", "3gp", "webm"]);
const audioExtensions = new Set(["mp3", "m4a", "wav", "aac", "flac", "aiff", "caf", "ogg", "opus"]);

export function mediaRef(source: string, kind?: MediaKind): MediaRef {
  return { source, kind: kind ?? mediaKindFor(source) };
}

export function isRemoteURL(r: MediaRef): boolean { return /^https?:\/\//i.test(r.source); }
export function isDataURL(r: MediaRef): boolean { return /^data:/i.test(r.source); }
/** 服务器本机上的文件（需要走带鉴权的下载接口） */
export function isServerPath(r: MediaRef): boolean { return !isRemoteURL(r) && !isDataURL(r); }

export function mediaFileName(r: MediaRef): string {
  if (isDataURL(r)) return `image.${mediaExtension(r) || "png"}`;
  let path = r.source;
  if (isRemoteURL(r)) { try { path = new URL(r.source).pathname; } catch { /* 原样 */ } }
  const last = path.split("/").filter(Boolean).pop() ?? path;
  try { return decodeURIComponent(last); } catch { return last; }
}

/** 小写扩展名；data URL 取 MIME 子类型 */
export function mediaExtension(r: MediaRef): string {
  if (isDataURL(r)) {
    const mime = r.source.slice(5).split(/[;,]/)[0] ?? "";
    const sub = (mime.split("/").pop() ?? "").toLowerCase();
    return sub === "jpeg" ? "jpg" : sub.replace("svg+xml", "svg");
  }
  return pathExtension(r.source);
}

export function mediaKindFor(source: string): MediaKind {
  const lower = source.toLowerCase();
  if (lower.startsWith("data:image/")) return lower.startsWith("data:image/svg") ? "file" : "image";
  const ext = pathExtension(source);
  if (imageExtensions.has(ext)) return "image";
  if (videoExtensions.has(ext)) return "video";
  if (audioExtensions.has(ext)) return "audio";
  return "file";
}

function pathExtension(source: string): string {
  let s = source;
  if (source.includes("://")) {
    const cut = s.search(/[?#]/);
    if (cut >= 0) s = s.slice(0, cut);
  }
  const last = s.split("/").filter(Boolean).pop() ?? "";
  const dot = last.lastIndexOf(".");
  return dot < 0 ? "" : last.slice(dot + 1).toLowerCase();
}

/** 一条消息按顺序拆成文字段和媒体段 */
export type MessageSegment = { text: string } | { media: MediaRef[] };

const mediaExts = "png|jpe?g|gif|webp|bmp|heic|heif|tiff?|mp4|mov|m4v|3gp|mp3|m4a|wav|aac|flac";
// 与桌面端 MEDIA_DELIVERY_EXTS 对齐：无引号路径允许中间有空格，以已知扩展名收尾
const deliveryExts = [
  "png", "jpg", "jpeg", "gif", "webp", "bmp", "tiff", "svg", "mp4", "mov", "avi", "mkv", "webm", "3gp", "mp3", "m2a",
  "wav", "ogg", "opus", "m4a", "flac", "pdf", "docx", "doc", "odt", "rtf", "txt", "md", "epub", "xlsx", "xls", "ods",
  "csv", "tsv", "json", "xml", "yaml", "yml", "kmz", "kml", "geojson", "gpx", "pptx", "ppt", "odp", "key", "zip", "tar",
  "gz", "tgz", "bz2", "xz", "7z", "rar", "apk", "ipa", "html", "htm", "heic", "m4v",
].sort((a, b) => b.length - a.length).join("|");

const quoted = "`[^`\\n]+`|\"[^\"\\n]+\"|'[^'\\n]+'";

const mediaTag = new RegExp(
  "[`\"']?MEDIA:\\s*(" + quoted + "|(?:~/|/)\\S+?(?:[^\\S\\n]+\\S+?)*?\\.(?:" + deliveryExts
    + ")(?=[\\s`\"'*_,;:)\\]}]|MEDIA:|$)|\\S+)[`\"']?", "gi");
const imageRef = new RegExp("@image:(" + quoted + "|\\S+)", "g");
/** 用户发的文件：`@file:相对或绝对路径` */
const fileRef = new RegExp("(?<![\\w/])@file:(" + quoted + "|\\S+)", "g");
const markdownImage = /!\[[^\]\n]*\]\(\s*<?([^)\s>]+)>?(?:\s+"[^"\n]*")?\s*\)/g;
const markdownMediaLink = new RegExp(
  "(?<!!)\\[([^\\]\\n]+)\\]\\(\\s*<?((?:https?://|/|~/)[^)\\s>]+?\\.(?:" + mediaExts + ")(?:[?#][^)\\s>]*)?)>?\\s*\\)", "gi");
const dataImage = /data:image\/[A-Za-z0-9.+-]+;base64,[A-Za-z0-9+/=]+/g;
const bareURL = new RegExp(
  "(?<![(\\[<\"'=])https?://[^\\s)<>\\]\"'`]+?\\.(?:" + mediaExts + ")(?:[?#][^\\s)<>\\]\"'`]*)?(?=[\\s)\\]>,;!?，。、）]|\\.(?:\\s|$)|$)", "gi");

function mightContainMedia(text: string): boolean {
  return text.includes("MEDIA:") || text.includes("@image:") || text.includes("@file:") || text.includes("](")
    || text.includes("data:image") || text.includes("://");
}

function looksLikeLocation(s: string): boolean {
  const l = s.toLowerCase();
  return s.startsWith("/") || s.startsWith("~/") || l.startsWith("http://") || l.startsWith("https://") || l.startsWith("data:image/");
}

function isPlaceholder(s: string): boolean {
  return ["[image]", "[screenshot]", "[audio]", "[video]", "[file]"].includes(s.toLowerCase());
}

function unquote(raw: string): string {
  const t = raw.trim();
  if (t.length >= 2 && t[0] === t[t.length - 1] && "`\"'".includes(t[0])) return t.slice(1, -1);
  // 句末标点不属于路径
  return t.replace(/^[，。；、,;]+|[，。；、,;]+$/g, "");
}

/** 从一行里取出媒体，返回剩下的文字 */
function extract(line: string): [string, MediaRef[]] {
  if (!mightContainMedia(line)) return [line, []];
  let s = line;
  const found: [number, MediaRef][] = [];

  const take = (regex: RegExp, group: number, replacement: (full: string) => string = () => "", isFileRef = false) => {
    let offset = 0;
    let result = "";
    let last = 0;
    regex.lastIndex = 0;
    for (const m of s.matchAll(regex)) {
      const index = m.index ?? 0;
      const raw = m[group] ?? "";
      const value = unquote(raw);
      result += s.slice(last, index);
      const full = m[0];
      if (looksLikeLocation(value) || value.toLowerCase().startsWith("file://") || isFileRef) {
        let source = value;
        if (value.toLowerCase().startsWith("file://")) { try { source = decodeURIComponent(new URL(value).pathname); } catch { /* 原样 */ } }
        found.push([index + offset, mediaRef(source)]);
        const rep = replacement(full);
        result += rep;
        offset += full.length - rep.length;
      } else {
        result += full;
      }
      last = index + full.length;
    }
    result += s.slice(last);
    s = result;
  };

  take(mediaTag, 1);
  take(imageRef, 1);
  take(fileRef, 1, undefined, true);
  take(markdownImage, 1);
  // [看视频](https://.../a.mp4) → 保留文字“看视频”，下方放播放器
  take(markdownMediaLink, 2, (full) => {
    const open = full.indexOf("[");
    const close = full.indexOf("]");
    return open >= 0 && close > open ? full.slice(open + 1, close) : "";
  });
  take(dataImage, 0);
  // 裸网址保留在文字里（可以点），同时显示预览
  bareURL.lastIndex = 0;
  for (const m of s.matchAll(bareURL)) found.push([m.index ?? 0, mediaRef(m[0])]);

  const refs = found.sort((a, b) => a[0] - b[0]).map((f) => f[1]);
  return [refs.length === 0 ? line : s, refs];
}

/**
 * 从 Hermes 的消息文本里找出媒体：
 * - `MEDIA:/path/to/file.png`（Hermes 投递文件的标准写法）
 * - `@image:/path`（用户发送的图片在历史里的持久化形式）、`@file:路径`
 * - Markdown 图片、指向图片/视频的 Markdown 链接、裸的图片/视频网址、data URL
 */
export function parseMessageMedia(text: string): MessageSegment[] {
  if (!mightContainMedia(text)) return text ? [{ text }] : [];
  const segments: MessageSegment[] = [];
  let buffer: string[] = [];
  const seen = new Set<string>();
  let inFence = false;

  const flushText = () => {
    const joined = buffer.join("\n").replace(/^\n+|\n+$/g, "");
    buffer = [];
    if (joined.trim()) segments.push({ text: joined });
  };

  for (const line of text.replace(/\r\n/g, "\n").split("\n")) {
    const trimmed = line.trim();
    const fence = trimmed.startsWith("```") || trimmed.startsWith("~~~");
    if (fence) inFence = !inFence;
    if (inFence || fence) { buffer.push(line); continue; }
    const [rest, found] = extract(line);
    const refs = found.filter((r) => { if (seen.has(r.source)) return false; seen.add(r.source); return true; });
    const restTrimmed = rest.trim();
    const keepLine = restTrimmed !== "" && !(found.length > 0 && isPlaceholder(restTrimmed));
    if (keepLine) buffer.push(found.length === 0 ? line : rest);
    else if (found.length === 0) buffer.push(line);
    if (refs.length === 0) continue;
    flushText();
    const last = segments[segments.length - 1];
    if (last && "media" in last) last.media.push(...refs);
    else segments.push({ media: refs });
  }
  flushText();
  return dropPlaceholders(dropRedundantDataImages(segments));
}

/** 只要媒体，不要文字 */
export function mediaRefsIn(text: string): MediaRef[] {
  return parseMessageMedia(text).flatMap((seg) => ("media" in seg ? seg.media : []));
}

/** 只要文字（去掉 MEDIA: 之类的标记），用于复制 */
export function plainMessageText(text: string): string {
  return parseMessageMedia(text).flatMap((seg) => ("text" in seg ? [seg.text] : [])).join("\n\n");
}

/** 工具结果里的媒体（image_generate 的 `image`、video_generate 的 `video` 等） */
export function mediaRefsInToolResult(result: Json | undefined): MediaRef[] {
  if (result === undefined || result === null) return [];
  if (typeof result === "string") return result.includes("MEDIA:") ? mediaRefsIn(result) : [];
  const o = obj(result);
  if (!o) return [];
  const out: MediaRef[] = [];
  const add = (value: Json | undefined, kind: MediaKind | undefined) => {
    const items = arr(value);
    if (items) { for (const item of items) add(item, kind); return; }
    const raw = str(value) ?? str(obj(value)?.url) ?? str(obj(value)?.path);
    const sv = raw?.trim();
    if (!sv || !looksLikeLocation(sv) || typeof value === "number" || typeof value === "boolean") return;
    const guessed = mediaKindFor(sv);
    // 通用字段（path/url/file）只认图片和视频，免得 read_file 之类的结果也冒出文件卡片
    if (kind === undefined && guessed !== "image" && guessed !== "video") return;
    const ref = mediaRef(sv, guessed === "file" ? (kind ?? "file") : guessed);
    if (!out.some((r) => r.source === ref.source && r.kind === ref.kind)) out.push(ref);
  };
  add(o.image, "image");
  add(o.images, "image");
  add(o.video, "video");
  add(o.videos, "video");
  add(o.audio, "audio");
  add(o.image_path, "image");
  add(o.video_path, "video");
  add(o.screenshot_path, "image");
  for (const key of ["path", "file", "file_path", "output_path", "url", "public_url"]) add(o[key], undefined);
  return out;
}

/** 历史里同一张图可能同时有 `@image:路径` 和内联的 data URL，只留路径那份 */
function dropRedundantDataImages(segments: MessageSegment[]): MessageSegment[] {
  const all = segments.flatMap((seg) => ("media" in seg ? seg.media : []));
  if (!all.some((r) => r.kind === "image" && !isDataURL(r)) || !all.some(isDataURL)) return segments;
  return segments.flatMap((seg): MessageSegment[] => {
    if (!("media" in seg)) return [seg];
    const kept = seg.media.filter((r) => !isDataURL(r));
    return kept.length ? [{ media: kept }] : [];
  });
}

function dropPlaceholders(segments: MessageSegment[]): MessageSegment[] {
  if (!segments.some((seg) => "media" in seg)) return segments;
  return segments.flatMap((seg): MessageSegment[] => {
    if (!("text" in seg)) return [seg];
    const lines = seg.text.split("\n").filter((l) => !isPlaceholder(l.trim()));
    const joined = lines.join("\n").replace(/^\n+|\n+$/g, "");
    return joined.trim() ? [{ text: joined }] : [];
  });
}
